import React, { useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../config/axios";

const LISTING_AGREEMENT_OPTIONS = [
  {
    value: "seller-representation",
    label: "Listing Agreement - Seller Representation Agreement (Form 200)",
  },
  {
    value: "landlord-representation",
    label: "Listing Agreement - Landlord Representation Agreement (Form 210)",
  },
  {
    value: "listing-agreement-commercial-520",
    label: "Listing Agreement Commercial (Form 520)",
  },
  {
    value: "listing-agreement-commercial-594",
    label: "Listing Agreement Commercial (Form 594)",
  },
];

const TRADE_FORM_OPTIONS = [
  {
    value: "agreement-to-lease",
    label: "Agreement to lease Residential (Form 400)",
  },
  {
    value: "agreement-purchase-sale-condo-resale",
    label: "Agreement of Purchase and Sale - Condominium (Form 101)",
  },
  {
    value: "agreement-purchase-sale",
    label: "Agreement of Purchase and Sale - Freehold (Form 100)",
  },
  {
    value: "agreement-purchase-sale-common-elements-potl-111",
    label:
      "Agreement of Purchase and Sale - Common Elements - POTL (Form 111)",
  },
  {
    value: "agreement-purchase-sale-commercial-502",
    label: "Agreement of Purchase and Sale Commercial (Form 502)",
  },
  {
    value: "agreement-purchase-sale-commercial-501",
    label: "Agreement of Purchase and Sale Commercial (Form 501)",
  },
];

const PdfUploadPanel = ({
  documentType,
  onParsed,
  compact = false,
  autoSaveDraft = false,
}) => {
  const [isParsing, setIsParsing] = useState(false);
  const [busyLabel, setBusyLabel] = useState("Reading PDF...");
  const [agreementType, setAgreementType] = useState("");
  const needsFormType =
    documentType === "listing" || documentType === "trade";
  const formOptions =
    documentType === "trade" ? TRADE_FORM_OPTIONS : LISTING_AGREEMENT_OPTIONS;

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (file.type !== "application/pdf") {
      toast.error("Please upload a PDF file.");
      return;
    }
    if (needsFormType && !agreementType) {
      toast.error("Select the form type first.");
      return;
    }

    let progressToastId = null;
    let progressTimer = null;
    try {
      setIsParsing(true);
      // OneDrive / network placeholders can hang forever before the request
      // even leaves the browser — read into memory first with a hard limit.
      setBusyLabel("Loading PDF...");
      const fileBuffer = await Promise.race([
        file.arrayBuffer(),
        new Promise((_, reject) => {
          setTimeout(
            () =>
              reject(
                new Error(
                  "DISK_TIMEOUT: Could not load the PDF from disk. If it is in OneDrive, make it available offline (or copy to Desktop) and try again.",
                ),
              ),
            45000,
          );
        }),
      ]);
      const pdfBlob = new Blob([fileBuffer], { type: "application/pdf" });
      // Chunk uploads stay under nginx's ~1mb body limit (413 Payload Too Large).
      const CHUNK_BYTES = 400 * 1024;
      const totalChunks = Math.max(
        1,
        Math.ceil(fileBuffer.byteLength / CHUNK_BYTES),
      );
      const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      const fileToBase64Chunk = (blob) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const result = String(reader.result || "");
            const base64 = result.includes(",")
              ? result.split(",")[1]
              : result;
            resolve(base64 || "");
          };
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });

      setBusyLabel("Uploading PDF...");
      progressTimer = setTimeout(() => {
        progressToastId = toast.info(
          "Still reading the DigiSign PDF — Form 101 usually finishes in about 1–2 minutes.",
          { autoClose: false },
        );
      }, 25000);

      let startRes = null;
      for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex += 1) {
        const start = chunkIndex * CHUNK_BYTES;
        const end = Math.min(fileBuffer.byteLength, start + CHUNK_BYTES);
        const chunkBase64 = await fileToBase64Chunk(
          pdfBlob.slice(start, end),
        );
        setBusyLabel(
          totalChunks > 1
            ? `Uploading PDF… (${chunkIndex + 1}/${totalChunks})`
            : "Uploading PDF...",
        );
        startRes = await axiosInstance.post(
          "/pdf/parse/chunk",
          {
            uploadId,
            fileName: file.name || "upload.pdf",
            mimeType: "application/pdf",
            chunkIndex,
            totalChunks,
            chunkBase64,
            documentType,
            agreementType: agreementType || "",
          },
          {
            timeout: 120000,
            validateStatus: (s) => (s >= 200 && s < 300) || s === 202,
          },
        );
      }

      let parsedData = startRes?.data;
      if (startRes?.data?.jobId) {
        setBusyLabel("Reading PDF...");
        const jobId = startRes.data.jobId;
        const deadline = Date.now() + 300000;
        parsedData = null;
        while (Date.now() < deadline) {
          await new Promise((r) => setTimeout(r, 2000));
          const jobRes = await axiosInstance.get(`/pdf/parse/jobs/${jobId}`, {
            timeout: 30000,
            validateStatus: (s) => s < 500,
          });
          if (jobRes.status >= 400) {
            const err = new Error(
              jobRes.data?.message || "Could not read that PDF.",
            );
            err.response = jobRes;
            throw err;
          }
          if (jobRes.data?.status === "done" && jobRes.data.result) {
            parsedData = jobRes.data.result;
            break;
          }
        }
        if (!parsedData) {
          throw new Error(
            "PDF reading timed out. Copy the Form 101 to your Desktop, refresh, and try once more.",
          );
        }
      } else if (!parsedData?.trade && !parsedData?.listing) {
        throw new Error(
          startRes?.data?.message ||
            "Upload did not finish. Refresh and try again.",
        );
      }
      if (progressTimer) clearTimeout(progressTimer);
      if (progressToastId != null) toast.dismiss(progressToastId);
      setBusyLabel(autoSaveDraft ? "Saving draft..." : "Applying fields...");
      const parsedResult = await onParsed(parsedData, {
        file: new File([pdfBlob], file.name || "upload.pdf", {
          type: "application/pdf",
        }),
        agreementType,
      });
      if (parsedResult?.agentMissing) {
        // Error toast already shown by the form handler
      } else if (parsedResult?.autoSaved) {
        if (parsedData.source === "parser") {
          toast.warning(
            "The PDF was read with the backup parser. Check the draft fields."
          );
        }
        // Success toast is shown by the form handler after save.
      } else if (parsedData.source === "parser") {
        toast.warning(
          "The PDF was read with the backup parser. Check the fields — the AI extractor was not available on the server."
        );
      } else {
        toast.success("PDF fields were extracted. Review the form, then save.");
      }
    } catch (err) {
      const msg = String(err?.message || "");
      const diskTimeout = msg.startsWith("DISK_TIMEOUT:");
      const timedOut =
        err.code === "ECONNABORTED" || /timeout/i.test(msg);
      const tooLarge =
        err.response?.status === 413 ||
        /413|payload too large|entity too large/i.test(msg);
      toast.error(
        err.response?.data?.message ||
          (diskTimeout
            ? msg.replace(/^DISK_TIMEOUT:\s*/, "")
            : timedOut
              ? "PDF reading timed out. Copy the Form 101 to your Desktop (not OneDrive-only), refresh the page, and try once — it usually finishes in 1–2 minutes."
              : tooLarge
                ? "That PDF is too large for the server upload limit. Compress it or try again after the latest update."
                : "Could not read that PDF."),
      );
    } finally {
      if (progressTimer) clearTimeout(progressTimer);
      if (progressToastId != null) toast.dismiss(progressToastId);
      setIsParsing(false);
      setBusyLabel("Reading PDF...");
    }
  };

  return (
    <div
      className={`border border-dashed border-blue-300 bg-blue-50 rounded-lg p-4 ${
        compact ? "mb-4" : "mb-6"
      }`}
    >
      <div className="flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <p className="font-semibold text-blue-900">
              Upload {documentType === "trade" ? "trade" : "listing"} PDF
            </p>
            <p className="text-sm text-gray-600">
              {documentType === "trade"
                ? autoSaveDraft
                  ? "Select the form type, then upload. The trade is saved as a draft and the APS is stored in Documents automatically."
                  : "Select the form type, then upload. Review the filled fields before saving. The APS is saved to Documents when you save the trade — do not upload it again there."
                : autoSaveDraft
                  ? "Select the form type, then upload. The listing is saved as a draft and the PDF is stored in Documents automatically."
                  : "Select the form type, then upload. Review the filled fields before saving. The listing PDF is saved to Documents when you save — then it appears under Compliance Review."}
            </p>
          </div>
          <label
            className={`inline-flex items-center justify-center px-4 py-2 rounded-md text-white ${
              isParsing || (needsFormType && !agreementType)
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-blue-900 cursor-pointer hover:bg-blue-800"
            }`}
          >
            {isParsing ? busyLabel : "Choose PDF"}
            <input
              type="file"
              accept="application/pdf"
              className="hidden"
              disabled={isParsing || (needsFormType && !agreementType)}
              onChange={handleFile}
            />
          </label>
        </div>
        {needsFormType && (
          <div className="max-w-xl">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Form type <span className="text-red-600">*</span>
            </label>
            <select
              value={agreementType}
              onChange={(e) => setAgreementType(e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700"
            >
              <option value="">Select form type</option>
              {formOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
    </div>
  );
};

export default PdfUploadPanel;
